import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { toast } from 'sonner';
import { debounce } from 'lodash';
import {
  Plus, Trash2, ChevronUp, ChevronDown, Sparkles, MessageSquare, X, AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import Spinner from '@/components/Spinner';
import { SearchInput } from '@/components/SearchInput';
import { Pagination } from '@/components/shared/Pagination';
import { cn } from '@/lib/utils';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useListLinks, useUpdateAiSuggestions, useDeleteAiSuggestions, useRegenerateAiSuggestions,
  useUpdateSingleLinkOptions,
} from '@/hooks/useBot';
import { ILinkForCTA } from '@/types/bots';
import { handleShowError } from '@/utils';

interface IPageMessagesManagerProps {
  token: string;
  aiSuggestionsEnabled: boolean;
}

type ItemStatusTone = 'active' | 'overridden' | 'saved';

const WELCOME_SOFT_LIMIT = 170;
const PROMPT_SOFT_LIMIT = 100;
const MAX_ITEMS = 10;

// Strip the origin so the list reads as page paths, not full URLs.
const toPath = (url: string) => {
  try {
    const u = new URL(url);
    return (u.pathname + u.search) || '/';
  } catch {
    return url;
  }
};

const moveItem = (arr: string[], from: number, to: number): string[] => {
  if (to < 0 || to >= arr.length) return arr;
  const next = [...arr];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

const hasPopupCopyOverride = (link: ILinkForCTA | null): boolean => (
  [link?.cached_content, link?.button_text]
    .some((value) => typeof value === 'string' && value.trim().length > 0)
);

const PageMessagesManager: React.FC<IPageMessagesManagerProps> = ({
  token,
  aiSuggestionsEnabled,
}) => {
  const {
    data, isLoading, isFetching, queryParams, setQueryParams, refetch,
  } = useListLinks(token);
  const links: ILinkForCTA[] = data?.data ?? [];

  const updateMut = useUpdateAiSuggestions();
  const deleteMut = useDeleteAiSuggestions();
  const regenerateMut = useRegenerateAiSuggestions();
  const updatePopupMut = useUpdateSingleLinkOptions();

  const [searchText, setSearchText] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [welcomes, setWelcomes] = useState<string[]>([]);
  const [prompts, setPrompts] = useState<string[]>([]);
  const [baseline, setBaseline] = useState<{ w: string; p: string }>({ w: '[]', p: '[]' });
  const [confirmClear, setConfirmClear] = useState(false);
  const [confirmRemovePopupOverride, setConfirmRemovePopupOverride] = useState(false);

  const selected = useMemo(
    () => links.find((l) => l.link_id === selectedId) ?? null,
    [links, selectedId],
  );

  // Auto-select the first page once the list loads and nothing is selected.
  useEffect(() => {
    if (selectedId === null && links.length > 0) {
      setSelectedId(links[0].link_id);
    }
  }, [links, selectedId]);

  // Hydrate the editor whenever the selected page (or its server data) changes.
  useEffect(() => {
    const w = Array.isArray(selected?.ai_welcome_suggestions) ? selected!.ai_welcome_suggestions! : [];
    const p = Array.isArray(selected?.ai_prompt_suggestions) ? selected!.ai_prompt_suggestions! : [];
    setWelcomes(w);
    setPrompts(p);
    setBaseline({ w: JSON.stringify(w), p: JSON.stringify(p) });
  }, [selected?.link_id, selected?.ai_suggestions_generated_at, selected?.ai_suggestions_manual]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = JSON.stringify(welcomes) !== baseline.w || JSON.stringify(prompts) !== baseline.p;
  const hasSavedPageWelcome = Array.isArray(selected?.ai_welcome_suggestions)
    && selected.ai_welcome_suggestions.some((message) => message.trim().length > 0);
  const canUseSavedPageWelcome = aiSuggestionsEnabled && hasSavedPageWelcome;

  const handleSearch = useMemo(
    () => debounce((value: string) => {
      setQueryParams((prev) => ({ ...prev, page: 1, searchText: value }));
    }, 400),
    [setQueryParams],
  );

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    handleSearch(e.target.value);
  };

  const handleSave = () => {
    if (!selected) return;
    updateMut.mutate(
      {
        linkId: selected.link_id,
        ai_welcome_suggestions: welcomes.map((s) => s.trim()).filter(Boolean),
        ai_prompt_suggestions: prompts.map((s) => s.trim()).filter(Boolean),
      },
      {
        onSuccess: (res) => {
          setWelcomes(res.ai_welcome_suggestions);
          setPrompts(res.ai_prompt_suggestions);
          setBaseline({
            w: JSON.stringify(res.ai_welcome_suggestions),
            p: JSON.stringify(res.ai_prompt_suggestions),
          });
          toast.success('Page messages saved.');
        },
        onError: handleShowError,
      },
    );
  };

  const handleRegenerate = () => {
    if (!selected) return;
    regenerateMut.mutate(selected.link_id, {
      onSuccess: (res) => {
        setWelcomes(res.ai_welcome_suggestions);
        setPrompts(res.ai_prompt_suggestions);
        setBaseline({
          w: JSON.stringify(res.ai_welcome_suggestions),
          p: JSON.stringify(res.ai_prompt_suggestions),
        });
        toast.success('Regenerated with AI.');
      },
      onError: handleShowError,
    });
  };

  const handleClear = () => {
    if (!selected) return;
    deleteMut.mutate(selected.link_id, {
      onSuccess: () => {
        setWelcomes([]);
        setPrompts([]);
        setBaseline({ w: '[]', p: '[]' });
        setConfirmClear(false);
        toast.success('Page messages cleared. This page now uses your default greeting.');
      },
      onError: (err) => { setConfirmClear(false); handleShowError(err); },
    });
  };

  const handleRemovePopupOverride = async () => {
    if (!selected) return;

    try {
      // The mutation endpoint replaces the complete CTA configuration. Refresh the
      // selected row immediately before writing so the preservation payload cannot
      // come from this list query's one-minute cache.
      const refreshed = await refetch();
      const freshSelected = refreshed.data?.data?.find(
        (link: ILinkForCTA) => link.link_id === selected.link_id,
      );
      if (!freshSelected) throw new Error('Unable to refresh this page before updating it.');

      updatePopupMut.mutate(
        {
          linkId: freshSelected.link_id,
          video_link: freshSelected.video_link || null,
          // Preserve the page's CTA buttons. This action only removes the custom
          // headline/greeting that outranks the welcome message below.
          options: {
            option_a: freshSelected.options?.option_a || null,
            option_b: freshSelected.options?.option_b || null,
            option_c: freshSelected.options?.option_c || null,
          },
          popup_headline: '',
          popup_greeting: '',
        },
        {
          onSuccess: () => {
            setConfirmRemovePopupOverride(false);
            toast.success(
              canUseSavedPageWelcome
                ? 'Popup copy override removed. The page welcome message is now active.'
                : 'Popup copy override removed. This page now uses the site-wide popup copy.',
            );
          },
          onError: (err) => {
            setConfirmRemovePopupOverride(false);
            handleShowError(err);
          },
        },
      );
    } catch (err) {
      setConfirmRemovePopupOverride(false);
      handleShowError(err);
    }
  };

  const busy = updateMut.isPending || regenerateMut.isPending || deleteMut.isPending
    || updatePopupMut.isPending;

  const renderList = () => {
    if (isLoading) {
      return <div className='flex justify-center py-10'><Spinner /></div>;
    }
    if (links.length === 0) {
      return (
        <p className='text-sm text-muted-foreground px-3 py-6 text-center'>
          {queryParams.searchText ? 'No pages match your search.' : 'No pages found yet.'}
        </p>
      );
    }
    return (
      <ul className='flex flex-col'>
        {links.map((l) => {
          const hasMsgs = (l.ai_welcome_suggestions?.length || 0) > 0 || (l.ai_prompt_suggestions?.length || 0) > 0;
          const hasPopupOverride = hasPopupCopyOverride(l);
          const active = l.link_id === selectedId;
          return (
            <li key={l.link_id}>
              <button
                type='button'
                onClick={() => setSelectedId(l.link_id)}
                className={`w-full text-left px-3 py-2.5 border-l-2 transition-colors ${
                  active
                    ? 'border-blue-600 bg-blue-50/60'
                    : 'border-transparent hover:bg-slate-50'
                }`}
              >
                <div className='flex items-center gap-2'>
                  <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${hasMsgs ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                  <span className='text-sm text-slate-800 truncate' title={l.link}>{toPath(l.link)}</span>
                  <span className='ml-auto flex shrink-0 items-center gap-1'>
                    {hasPopupOverride ? (
                      <Badge variant='outline' className='border-amber-300 bg-amber-50 text-amber-700 text-[10px] px-1.5 py-0'>Popup override</Badge>
                    ) : null}
                    {l.ai_suggestions_manual ? (
                      <Badge variant='outline' className='text-[10px] px-1.5 py-0'>Edited</Badge>
                    ) : null}
                  </span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  const renderEditor = () => {
    if (!selected) {
      return (
        <div className='flex flex-col items-center justify-center h-full text-center gap-2 py-16'>
          <MessageSquare className='w-8 h-8 text-slate-300' />
          <p className='text-sm text-muted-foreground'>Select a page to manage its welcome message and prompts.</p>
        </div>
      );
    }

    const generatedAt = selected.ai_suggestions_generated_at;
    const isManual = selected.ai_suggestions_manual === true;
    const hasPopupOverride = hasPopupCopyOverride(selected);
    const welcomeStatus: { label: string; tone: ItemStatusTone } = hasPopupOverride
      ? { label: 'Overridden', tone: 'overridden' }
      : aiSuggestionsEnabled
        ? { label: 'Greeting', tone: 'active' }
        : { label: 'Saved', tone: 'saved' };

    return (
      <div className='flex flex-col h-full min-h-0'>
        {/* Header */}
        <div className='flex items-start justify-between gap-4 px-6 py-4 border-b border-border'>
          <div className='min-w-0'>
            <p className='text-sm font-medium text-foreground truncate' title={selected.link}>{toPath(selected.link)}</p>
            <div className='text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap'>
              {generatedAt
                ? <span>Last updated {dayjs(generatedAt).format('DD MMM YYYY, HH:mm')}</span>
                : <span>No AI messages generated yet</span>}
              {generatedAt ? (
                <Badge variant={isManual ? 'secondary' : 'outline'} className='text-[10px] px-1.5 py-0'>
                  {isManual ? 'Manually edited' : 'AI-generated'}
                </Badge>
              ) : null}
            </div>
          </div>
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={handleRegenerate}
            disabled={busy}
            className='shrink-0 gap-1.5'
          >
            {regenerateMut.isPending ? <Spinner /> : <Sparkles className='w-3.5 h-3.5' />}
            Regenerate with AI
          </Button>
        </div>

        <div className='flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-5'>
          {hasPopupOverride ? (
            <div className='flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900'>
              <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0' />
              <div className='min-w-0 flex-1'>
                <p className='text-sm font-semibold'>Pre-chat popup override active</p>
                <p className='mt-0.5 text-xs leading-relaxed text-amber-800'>
                  This page has a custom headline or greeting in CTA Settings, so the welcome
                  message below is not currently shown in the pre-chat popup. This popup override
                  does not affect prompt suggestion settings.
                </p>
                {!aiSuggestionsEnabled ? (
                  <p className='mt-1.5 text-xs leading-relaxed text-amber-800'>
                    AI-generated page welcome is off in Settings → Chat Behavior, so the saved
                    welcome and prompt suggestions will not appear. Removing this override will
                    use the site-wide popup copy instead of the saved welcome below.
                  </p>
                ) : null}
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={() => setConfirmRemovePopupOverride(true)}
                  disabled={busy || dirty || isFetching}
                  title={dirty ? 'Save your welcome and prompt changes first' : undefined}
                  className='mt-2 h-7 border-amber-300 bg-white text-xs text-amber-900 hover:bg-amber-100'
                >
                  {canUseSavedPageWelcome ? 'Use the saved page welcome' : 'Remove popup copy override'}
                </Button>
                {dirty ? (
                  <p className='mt-1.5 text-[11px] text-amber-700'>
                    Save your welcome and prompt changes before removing this override.
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* Welcome messages */}
          <section className='space-y-2'>
            <div>
              <p className='text-sm font-semibold text-foreground'>Welcome messages</p>
              <p className='text-xs text-muted-foreground mt-0.5'>
                Page-specific greeting used by the pre-chat popup when AI page suggestions are
                enabled and CTA Settings does not provide custom popup copy.{' '}
                <strong>
                  {hasPopupOverride
                    ? 'Currently overridden'
                    : aiSuggestionsEnabled
                      ? 'Top message is live'
                      : 'Saved, but AI page suggestions are off'}
                </strong>;
                the rest are saved alternatives.
              </p>
            </div>
            <div className='space-y-1'>
              {welcomes.map((w, i) => (
                <ItemRow
                  key={`w-${i}`}
                  value={w}
                  index={i}
                  total={welcomes.length}
                  statusLabel={i === 0 ? welcomeStatus.label : undefined}
                  statusTone={i === 0 ? welcomeStatus.tone : undefined}
                  softLimit={WELCOME_SOFT_LIMIT}
                  multiline
                  onChange={(val) => setWelcomes((arr) => arr.map((x, idx) => (idx === i ? val : x)))}
                  onRemove={() => setWelcomes((arr) => arr.filter((_, idx) => idx !== i))}
                  onMoveUp={() => setWelcomes((arr) => moveItem(arr, i, i - 1))}
                  onMoveDown={() => setWelcomes((arr) => moveItem(arr, i, i + 1))}
                />
              ))}
              {welcomes.length < MAX_ITEMS && (
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={() => setWelcomes((arr) => [...arr, ''])}
                  className='gap-1.5'
                >
                  <Plus className='w-3.5 h-3.5' /> Add welcome message
                </Button>
              )}
            </div>
          </section>

          {/* Prompt suggestions */}
          <section className='space-y-2'>
            <div>
              <p className='text-sm font-semibold text-foreground'>In-chat prompt suggestions</p>
              <p className='text-xs text-muted-foreground mt-0.5'>
                Quick-reply chips shown above the chat input after a visitor opens the full chat.{' '}
                <strong>
                  {aiSuggestionsEnabled
                    ? 'First three are live'
                    : 'Saved, but AI page suggestions are off'}
                </strong>;
                {' '}the rest are saved alternatives.
              </p>
            </div>
            <div className='space-y-1'>
              {prompts.map((p, i) => (
                <ItemRow
                  key={`p-${i}`}
                  value={p}
                  index={i}
                  total={prompts.length}
                  statusLabel={i < 3 ? (aiSuggestionsEnabled ? 'Live chip' : 'Saved') : undefined}
                  statusTone={i < 3 ? (aiSuggestionsEnabled ? 'active' : 'saved') : undefined}
                  softLimit={PROMPT_SOFT_LIMIT}
                  onChange={(val) => setPrompts((arr) => arr.map((x, idx) => (idx === i ? val : x)))}
                  onRemove={() => setPrompts((arr) => arr.filter((_, idx) => idx !== i))}
                  onMoveUp={() => setPrompts((arr) => moveItem(arr, i, i - 1))}
                  onMoveDown={() => setPrompts((arr) => moveItem(arr, i, i + 1))}
                />
              ))}
              {prompts.length < MAX_ITEMS && (
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={() => setPrompts((arr) => [...arr, ''])}
                  className='gap-1.5'
                >
                  <Plus className='w-3.5 h-3.5' /> Add prompt
                </Button>
              )}
            </div>
          </section>
        </div>

        {/* Footer actions */}
        <div className='flex items-center justify-between gap-3 px-6 py-4 border-t border-border'>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() => setConfirmClear(true)}
            disabled={busy || (welcomes.length === 0 && prompts.length === 0)}
            className='text-muted-foreground hover:text-destructive'
          >
            <Trash2 className='w-3.5 h-3.5 mr-1.5' /> Clear all
          </Button>
          <Button type='button' onClick={handleSave} disabled={!dirty || busy} className='gap-1.5'>
            {updateMut.isPending ? <Spinner /> : null}
            Save changes
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col lg:flex-row h-full divide-y lg:divide-y-0 lg:divide-x divide-border">
      {/* Left: page picker */}
      <div className='lg:w-80 shrink-0 flex flex-col min-h-0'>
        <div className='p-3 border-b border-border'>
          <SearchInput placeholder='Search pages…' value={searchText} onChange={onSearchChange} isLoading={isFetching && !!queryParams.searchText} />
        </div>
        <div className='flex-1 min-h-0 overflow-y-auto'>{renderList()}</div>
        <Pagination
          className="border-t border-border px-3 py-2"
          currentPage={Number(queryParams.page ?? 1)}
          totalPages={Number(data?.totalPages ?? 1)}
          onPageChange={(page) => setQueryParams((prev) => ({ ...prev, page }))}
          showPageInfo={false}
        />
      </div>

      {/* Right: editor */}
      <div className='flex-1 min-h-0'>{renderEditor()}</div>

      <AlertDialog open={confirmClear} onOpenChange={setConfirmClear}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all messages for this page?</AlertDialogTitle>
            <AlertDialogDescription>
              The AI welcome messages and prompts for this page will be removed. Visitors will see
              your default greeting instead until new ones are generated. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleClear} className='bg-red-600 hover:bg-red-700 focus:ring-red-600'>
              Clear
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmRemovePopupOverride} onOpenChange={setConfirmRemovePopupOverride}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove the popup copy override?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes only the page-specific headline and greeting from CTA Settings.
              Existing CTA buttons will be preserved, and{' '}
              {canUseSavedPageWelcome
                ? 'the saved top welcome message will become the pre-chat popup greeting.'
                : 'this page will fall back to the site-wide popup copy.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemovePopupOverride}
              disabled={isFetching || updatePopupMut.isPending}
            >
              {canUseSavedPageWelcome ? 'Use saved page welcome' : 'Remove override'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ---- single editable row -------------------------------------------------

interface IItemRowProps {
  value: string;
  index: number;
  total: number;
  statusLabel?: string;
  statusTone?: ItemStatusTone;
  softLimit: number;
  multiline?: boolean;
  onChange: (val: string) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

const ItemRow: React.FC<IItemRowProps> = ({
  value, index, total, statusLabel, statusTone, softLimit, multiline,
  onChange, onRemove, onMoveUp, onMoveDown,
}) => {
  const over = value.length > softLimit;
  const hasStatus = !!statusLabel;
  const isActive = statusTone === 'active';
  const isOverridden = statusTone === 'overridden';
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea to content height (starts at 1 row, grows as needed).
  React.useLayoutEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [value]);

  return (
    <div className={cn(
      'group flex items-center gap-2 rounded-md border-l-2 px-3 py-2 transition-colors',
      isOverridden
        ? 'border-amber-400 bg-amber-50/50'
        : isActive
        ? 'border-emerald-400 bg-emerald-50/40'
        : 'border-gray-200 bg-gray-50/60 hover:border-gray-300',
    )}>
      {/* Reorder controls */}
      <div className="flex flex-col shrink-0">
        <button
          type="button"
          onClick={onMoveUp}
          disabled={index === 0}
          title="Move up"
          className="p-0.5 text-gray-300 hover:text-gray-600 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronUp className="w-3 h-3" />
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={index === total - 1}
          title="Move down"
          className="p-0.5 text-gray-300 hover:text-gray-600 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronDown className="w-3 h-3" />
        </button>
      </div>

      {/* Input — auto-resize textarea for welcome messages, plain input for prompts */}
      <div className="flex-1 min-w-0">
        {multiline ? (
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={1}
            placeholder="Write a message…"
            className="w-full text-sm bg-transparent border-0 p-0 focus:outline-none focus:ring-0 resize-none overflow-hidden text-gray-800 placeholder:text-gray-400 leading-5"
          />
        ) : (
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Write a prompt…"
            className="w-full text-sm bg-transparent border-0 p-0 focus:outline-none focus:ring-0 text-gray-800 placeholder:text-gray-400"
          />
        )}
      </div>

      {/* Inline: live label + count + delete-on-hover */}
      <div className="flex items-center gap-2 shrink-0">
        {hasStatus && (
          <span className={cn(
            'text-[9px] font-bold tracking-widest uppercase select-none',
            isOverridden
              ? 'text-amber-700'
              : isActive
                ? 'text-emerald-600'
                : 'text-slate-500',
          )}>
            {statusLabel}
          </span>
        )}
        <span className={cn('text-[10px] tabular-nums', over ? 'text-amber-600' : 'text-gray-400')}>
          {value.length}/{softLimit}
        </span>
        <button
          type="button"
          onClick={onRemove}
          title="Remove"
          className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-opacity"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default PageMessagesManager;
